import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useLayoutContext } from "../context/LayoutContext";
import { API_URLS } from "../App";
import api from "../hooks/api";
import {
  CheckCircle2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  PackageX,
  Replace,
} from "lucide-react";
import ModalScreen from "../components/common/ModalScreen";
import {
  sortEquivalents,
  type EquivalentSortKey,
} from "../helpers/sortEquivalents";

const STOCK_THRESHOLD = 5;

interface EquivalentProduct {
  id: string;
  name: string;
  stock: number;
  brand?: string | null;
  categoryName?: string | null;
  cost_price?: number | null;
  rev4_price?: number | null;
  features?: { id?: string; name?: string; value?: string; order?: number }[];
}

const CompoundManageStockReplacement: React.FC<{}> = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { error, loading, setError, setSuccess, setLoading } =
    useLayoutContext();
  const [data, setData] = useState<ComponentsLowStock[]>([]);
  const [expandedComponents, setExpandedComponents] = useState<Set<string>>(
    new Set(),
  );
  const [equivalentsModal, setEquivalentsModal] = useState<{
    item: ComponentsLowStock;
  } | null>(null);
  const [equivalents, setEquivalents] = useState<EquivalentProduct[]>([]);
  const [equivalentsSort, setEquivalentsSort] =
    useState<EquivalentSortKey>("price");
  const [loadingEquivalents, setLoadingEquivalents] = useState(false);
  const [equivalentsError, setEquivalentsError] = useState<string | null>(null);
  const [selectedEquivalent, setSelectedEquivalent] =
    useState<EquivalentProduct | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    fromProductReference: string;
    componentTypeKey: string;
    toProductReference: string;
    compoundIds: string[];
  } | null>(null);

  const itemKey = (item: ComponentsLowStock) =>
    `${item.product_reference}:${item.component_type_key}`;

  const toggleComponent = (key: string) => {
    const newExpanded = new Set(expandedComponents);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedComponents(newExpanded);
  };

  const fetchLowStock = async (): Promise<boolean> => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await api.get(
        API_URLS.COMPOUNDS + "/_view/components_low_stock",
        { params: { maxStock: STOCK_THRESHOLD } },
      );
      setData(response.data || []);
      return true;
    } catch (err: any) {
      setError(err.message || "Failed to fetch low stock components");
      console.warn("Error fetching low stock components:", err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated()) {
      fetchLowStock();
    } else {
      navigate("/login", { state: { from: pathname } });
    }
  }, [isAuthenticated, pathname, navigate]);

  const openEquivalents = async (item: ComponentsLowStock) => {
    setEquivalentsModal({ item });
    setSelectedEquivalent(null);
    setEquivalents([]);
    setEquivalentsSort("price");
    setEquivalentsError(null);
    setLoadingEquivalents(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await api.get(
        API_URLS.COMPOUNDS + "/_view/component_stock_equivalents",
        {
          params: {
            productReference: item.product_reference,
            componentTypeKey: item.component_type_key,
          },
        },
      );
      setEquivalents(response.data || []);
    } catch (err: any) {
      setEquivalentsError(err.message || "Falha ao carregar equivalentes");
      setError(err.message || "Failed to fetch equivalents");
    } finally {
      setLoadingEquivalents(false);
    }
  };

  const requestReplace = (candidate: EquivalentProduct) => {
    if (!equivalentsModal) return;
    const { item } = equivalentsModal;
    setEquivalentsModal(null);
    setConfirmModal({
      fromProductReference: item.product_reference,
      componentTypeKey: item.component_type_key,
      toProductReference: candidate.id,
      compoundIds: item.compounds.map((c) => c.compound_id),
    });
  };

  const executeReplace = async () => {
    if (!confirmModal) return;
    const {
      fromProductReference,
      componentTypeKey,
      toProductReference,
      compoundIds,
    } = confirmModal;

    setConfirmModal(null);
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const response = await api.post(
        API_URLS.COMPOUNDS + "/_actions/replace_component",
        null,
        {
          params: {
            fromProductReference,
            componentTypeKey,
            toProductReference,
            compoundIds: compoundIds.join(","),
          },
        },
      );

      // Refresh first so the error/success toast set below isn't immediately cleared
      // by fetchLowStock()'s setError(null).
      const refreshed = await fetchLowStock();

      const updated = response.data.updatedCompounds ?? 0;
      const failed = response.data.failedCompounds ?? 0;
      if (!response.data.success || updated === 0) {
        setError(
          response.data.message ||
            "Falha ao substituir componente (0 máquinas)",
        );
      } else if (!refreshed) {
        setError(
          `Componente substituído em ${updated} computador(es), mas a lista não pôde ser atualizada`,
        );
      } else if (failed > 0) {
        setSuccess(
          `Componente substituído em ${updated} computador(es), ${failed} falha(s)!`,
        );
      } else {
        setSuccess(`Componente substituído em ${updated} computador(es)!`);
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Falha ao substituir componente",
      );
      console.error("Error replacing component:", err);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated()) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Acesso Negado
          </h1>
          <p className="text-gray-600">
            Você deve estar logado para visualizar esta página.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="flex flex-col gap-6">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <span className="text-3xl font-bold text-gray-900 tracking-tight">
              Componentes sem Stock (SAG)
            </span>
            <p className="text-gray-500 mt-1">
              Componentes com stock SAG igual ou abaixo de {STOCK_THRESHOLD}{" "}
              unidades, presentes nas máquinas do catálogo. Substitua por um
              equivalente com stock.
            </p>
          </div>
        </div>

        {/* Components Grid */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-600">
            A carregar componentes...
          </div>
        ) : data.length === 0 ? (
          error ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-600">
              Não foi possível carregar a lista de componentes.
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Nenhum Componente Sem Stock
              </h3>
              <p className="text-gray-600">
                Todos os componentes das máquinas têm stock suficiente no SAG.
              </p>
            </div>
          )
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {data.map((item) => {
              const key = itemKey(item);
              return (
                <div
                  key={key}
                  className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  {/* Component Header */}
                  <div className="border-b border-gray-200 px-6 py-4 bg-gray-50 rounded-t-lg flex flex-wrap items-center justify-between gap-3">
                    <button
                      type="button"
                      aria-expanded={expandedComponents.has(key)}
                      aria-controls={`${key}-machines`}
                      onClick={() => toggleComponent(key)}
                      className="flex items-center flex-1 min-w-0 text-left cursor-pointer"
                    >
                      {expandedComponents.has(key) ? (
                        <ChevronUp className="h-5 w-5 text-gray-600 mr-3 shrink-0" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-600 mr-3 shrink-0" />
                      )}
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                          {item.component_name || item.product_name}
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                          <span className="font-medium">Referência:</span>{" "}
                          {item.product_reference} •{" "}
                          <span className="font-medium">Produto:</span>{" "}
                          {item.product_name}
                        </p>
                      </div>
                    </button>
                    <div className="flex items-center gap-3">
                      <span
                        className={`tag is-light text-xs font-bold ${
                          item.erp_stock === 0 ? "is-danger" : "is-warning"
                        }`}
                      >
                        Stock SAG: {item.erp_stock}
                      </span>
                      <button
                        onClick={() => openEquivalents(item)}
                        className="flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm font-medium shadow-sm"
                      >
                        <Replace className="h-4 w-4 mr-2" />
                        Substituir por Equivalente ({item.compounds.length})
                      </button>
                    </div>
                  </div>

                  {/* Machines list */}
                  {expandedComponents.has(key) && (
                    <div id={`${key}-machines`} className="p-6 space-y-4">
                      <details className="group" open>
                        <summary className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-700 list-none">
                          Ver Computadores ({item.compounds.length})
                        </summary>
                        <div className="mt-3 max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-md p-3">
                          <ul className="space-y-1">
                            {item.compounds.map((compound, index) => (
                              <li key={index}>
                                <button
                                  onClick={() =>
                                    navigate(
                                      "/catalog/compound/" +
                                        encodeURIComponent(
                                          compound.compound_id,
                                        ),
                                    )
                                  }
                                  className="w-full text-left text-xs font-mono text-gray-700 hover:text-blue-600 hover:bg-blue-50 px-2 py-1 rounded cursor-pointer transition-colors"
                                >
                                  {compound.compound_id} •{" "}
                                  {compound.optional ? "Opcional" : "Base"} •{" "}
                                  {compound.status}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </details>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Equivalents Modal */}
      {equivalentsModal && (
        <ModalScreen
          title="Encontrar Equivalente"
          state="view"
          onClose={() => setEquivalentsModal(null)}
          footer={
            <div className="flex justify-end space-x-3 p-4">
              <button
                onClick={() => setEquivalentsModal(null)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={() =>
                  selectedEquivalent && requestReplace(selectedEquivalent)
                }
                disabled={!selectedEquivalent}
                className={`px-4 py-2 rounded-lg transition-colors font-medium ${
                  selectedEquivalent
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                Continuar
              </button>
            </div>
          }
        >
          <div className="p-4">
            {equivalentsModal && (
              <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">
                  Componente atual
                </p>
                <p className="text-sm">
                  <span className="font-medium text-gray-700">Referência:</span>{" "}
                  {equivalentsModal.item.product_reference}
                </p>
                <p className="text-sm">
                  <span className="font-medium text-gray-700">Modelo:</span>{" "}
                  {equivalentsModal.item.product_name}
                </p>
                <div className="flex gap-4 mt-1 text-xs text-gray-700">
                  {equivalentsModal.item.rev4_price != null && (
                    <span>
                      Preço Rev4:{" "}
                      {equivalentsModal.item.rev4_price.toLocaleString(
                        "pt-PT",
                        {
                          style: "currency",
                          currency: "EUR",
                        },
                      )}
                    </span>
                  )}
                  {equivalentsModal.item.cost_price != null && (
                    <span>
                      Preço Custo:{" "}
                      {equivalentsModal.item.cost_price.toLocaleString(
                        "pt-PT",
                        {
                          style: "currency",
                          currency: "EUR",
                        },
                      )}
                    </span>
                  )}
                </div>
                <p className="text-sm mt-1">
                  <span className="font-medium text-gray-700">Stock SAG:</span>{" "}
                  {equivalentsModal.item.erp_stock}
                </p>
              </div>
            )}
            {loadingEquivalents ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="animate-spin h-6 w-6 text-gray-500 mr-2" />
                A carregar equivalentes...
              </div>
            ) : equivalentsError ? (
              <div className="text-center py-12 text-gray-600">
                <PackageX className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                Falha ao carregar os equivalentes: {equivalentsError}
              </div>
            ) : equivalents.length === 0 ? (
              <div className="text-center py-12 text-gray-600">
                <PackageX className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                Não foram encontrados equivalentes com stock disponível.
              </div>
            ) : (
              <div>
                <div
                  className="flex items-center gap-2 mb-3"
                  role="group"
                  aria-label="Ordenar equivalentes"
                >
                  <span className="text-xs text-gray-500 font-medium">
                    Ordenar:
                  </span>
                  {(["price", "name", "stock"] as const).map((key) => (
                    <button
                      key={key}
                      type="button"
                      aria-pressed={equivalentsSort === key}
                      onClick={() => setEquivalentsSort(key)}
                      className={`px-2 py-1 text-xs rounded border transition-colors ${
                        equivalentsSort === key
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {key === "price"
                        ? "Preço"
                        : key === "name"
                          ? "Nome"
                          : "Stock"}
                    </button>
                  ))}
                </div>
                <div
                  className="space-y-2"
                  role="radiogroup"
                  aria-label="Equivalentes"
                >
                  {sortEquivalents(equivalents, equivalentsSort).map(
                    (candidate) => (
                      <label
                        key={candidate.id}
                        className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedEquivalent?.id === candidate.id
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        <input
                          type="radio"
                          name="equivalent"
                          value={candidate.id}
                          checked={selectedEquivalent?.id === candidate.id}
                          onChange={() => setSelectedEquivalent(candidate)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-gray-900">
                                {candidate.name}
                              </p>
                              <p className="text-xs text-gray-600">
                                {candidate.brand && `Marca: ${candidate.brand}`}
                                {candidate.categoryName &&
                                  ` • ${candidate.categoryName}`}
                              </p>
                            </div>
                            <span
                              className={`tag is-light text-xs font-bold ${
                                candidate.stock > 0 ? "is-success" : "is-danger"
                              }`}
                            >
                              Stock: {candidate.stock}
                            </span>
                          </div>
                          <div className="flex gap-4 mt-2 text-xs text-gray-700">
                            {candidate.rev4_price != null && (
                              <span>
                                Rev4:{" "}
                                {candidate.rev4_price.toLocaleString("pt-PT", {
                                  style: "currency",
                                  currency: "EUR",
                                })}
                              </span>
                            )}
                            {candidate.cost_price != null && (
                              <span>
                                Custo:{" "}
                                {candidate.cost_price.toLocaleString("pt-PT", {
                                  style: "currency",
                                  currency: "EUR",
                                })}
                              </span>
                            )}
                          </div>
                        </div>
                      </label>
                    ),
                  )}
                </div>
              </div>
            )}
          </div>
        </ModalScreen>
      )}

      {/* Confirmation Modal */}
      {confirmModal && (
        <ModalScreen
          title="Confirmar Substituição"
          state="confirm"
          onClose={() => setConfirmModal(null)}
          footer={
            <div className="flex justify-end space-x-3 p-4">
              <button
                onClick={() => setConfirmModal(null)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={executeReplace}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Confirmar
              </button>
            </div>
          }
        >
          <div className="p-4">
            <p className="text-gray-700 mb-4">
              Tem a certeza que pretende substituir este componente em{" "}
              <span className="font-bold">
                {confirmModal.compoundIds.length}
              </span>{" "}
              computador(es)?
            </p>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <p className="font-medium text-yellow-800 mb-2">Esta ação irá:</p>
              <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
                <li>Substituir a referência pelo equivalente selecionado</li>
                <li>Aplicar os preços ERP do novo produto</li>
                <li>Recalcular os preços da máquina (price engine)</li>
                <li>Remover qualquer preço redefinido</li>
              </ul>
            </div>
          </div>
        </ModalScreen>
      )}
    </div>
  );
};

export default CompoundManageStockReplacement;
